import { Component, computed, input } from '@angular/core';

import { TrustedCmsHtmlPipe } from '../../core/api/trusted-cms-html.pipe';

let instanceCount = 0;

/** Inputs arrive from CMS payloads that may be null/undefined or padded with whitespace. */
function trimmed(value: string | null | undefined): string {
  return (value ?? '').trim();
}

// Shared long-form content block — the "Complete Savings Guide" design from the store page:
// a bordered card with an optional heading + intro, the full CMS HTML, and a Read More / Read Less
// control. Drop it on any page that renders long content so they all share one design:
// <app-longform-content [heading]="..." [html]="..." />
//
// The content is always rendered in full; the collapse is a CSS clamp the checkbox lifts. It used
// to render a 500 character plain-text preview instead and only mount the real HTML after a click,
// which kept the bulk of the copy out of the server-rendered page entirely.
@Component({
  selector: 'app-longform-content',
  imports: [TrustedCmsHtmlPipe],
  templateUrl: './longform-content.html',
  styleUrl: './longform-content.scss',
})
export class LongformContent {
  private readonly previewLimit = 500;

  // Signal inputs with a trim transform. These previously used aliased `@Input('heading') set
  // headingInput(...)` setters purely to write into a same-named signal — which is exactly what
  // `input()` + `transform` does natively, and what the `no-input-rename` lint rule was flagging.
  readonly heading = input('', { transform: trimmed });
  readonly intro = input('', { transform: trimmed });
  readonly html = input('', { transform: trimmed });

  // Distinct per instance, so one "Read More" cannot toggle another block on the same page.
  // A module counter is stable across server and client render because components are created in
  // the same order in both, which is what hydration needs.
  protected readonly toggleId = `sav-longform-readmore-${(instanceCount += 1)}`;

  protected readonly plainText = computed(() => this.htmlToText(this.html()));

  protected readonly shouldShowToggle = computed(() => this.plainText().length > this.previewLimit);

  private htmlToText(html: string): string {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
